// Updated DisplayScreen.js without "+X more" text
import React, { useState, useEffect } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import { normalizeDancer } from '../utils/fieldCompatibility';
import './DisplayScreen.css';

//const _host = 'chappie-demo.novosense.africa';
const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const DisplayScreen = () => {
  const [displayData, setDisplayData] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [heats, setHeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [competitionThumbnail, setCompetitionThumbnail] = useState(null);
  const [backgroundDetails, setBackgroundDetails] = useState(null);
  const { selectedCompetition } = useCompetition();

  // Generate heats (same logic as frontend)
  const generateHeats = (event) => {
    const { dancers, dances, numHeats } = event;
    
    if (!dancers || dancers.length === 0) {
      return [];
    }
    if (!dances || dances.length === 0) {
      return [];
    }
    let actualNumHeats = numHeats || 0;
    if (!actualNumHeats || actualNumHeats === 0) {
      // Fix: Use default of 1 heat if numHeats is missing
      actualNumHeats = 1;
    }

    const generatedHeats = [];
    const sortedDancers = [...dancers].sort((a, b) => 
      parseInt(a.clothNumber) - parseInt(b.clothNumber)
    );
    
    dances.forEach((dance, danceIndex) => {
      const danceHeats = [];
      
      // Create a shifted version of dancers for each dance
      const shiftAmount = danceIndex * Math.ceil(dancers.length / 3);
      let dancersForThisDance = [...sortedDancers];
      
      if (danceIndex > 0) {
        switch (danceIndex % 5) {
          case 1:
            dancersForThisDance = [
              ...sortedDancers.slice(shiftAmount % sortedDancers.length),
              ...sortedDancers.slice(0, shiftAmount % sortedDancers.length)
            ];
            break;
          case 2:
            dancersForThisDance = [
              ...sortedDancers.slice(sortedDancers.length - shiftAmount % sortedDancers.length),
              ...sortedDancers.slice(0, sortedDancers.length - shiftAmount % sortedDancers.length)
            ];
            break;
          case 3:
            dancersForThisDance = [...sortedDancers].sort((a, b) => {
              return (parseInt(a.clothNumber) % 2) - (parseInt(b.clothNumber) % 2) || 
                     parseInt(a.clothNumber) - parseInt(b.clothNumber);
            });
            break;
          case 4:
            const even = sortedDancers.filter((_, i) => i % 2 === 0);
            const odd = sortedDancers.filter((_, i) => i % 2 === 1);
            dancersForThisDance = [...even, ...odd];
            break;
          default:
            break;
        }
      }
      
      // Calculate dancers per heat
      const dancersPerHeat = Array(actualNumHeats).fill(0)
        .map((_, i) => Math.floor(dancers.length / actualNumHeats) + (i < dancers.length % actualNumHeats ? 1 : 0));

      // Create heats for this dance
      let dancerIndex = 0;
      for (let heatIndex = 0; heatIndex < actualNumHeats; heatIndex++) {
        const heatDancers = [];
        
        for (let i = 0; i < dancersPerHeat[heatIndex]; i++) {
          if (dancerIndex < dancersForThisDance.length) {
            heatDancers.push(dancersForThisDance[dancerIndex]);
            dancerIndex++;
          }
        }
        
        danceHeats.push({
          heatNumber: heatIndex + 1,
          dancers: heatDancers
        });
      }
      
      generatedHeats.push({
        dance,
        heats: danceHeats
      });
    });

    return generatedHeats;
  };

  // Fetch display data from API
  const fetchDisplayData = async () => {
    try {
      console.log('🖥️ DEBUG: fetchDisplayData - calling API...');
      const response = await fetch('https://'+_host+':6003/api/'+_company+'/display');

      if (!response.ok) {
        console.error('🖥️ DEBUG: fetchDisplayData - API error:', response.status);
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log('🖥️ DEBUG: fetchDisplayData - API response data:', data);
      console.log('🖥️ DEBUG: background fields:', {
        backgroundImage: data?.backgroundImage,
        background_image: data?.background_image,
        backgroundId: data?.backgroundId,
        background_id: data?.background_id,
        backgroundType: data?.backgroundType,
        background_type: data?.background_type
      });

      // TEMPORARY FIX: If we have backgroundId but no background image, fetch it directly
      if (data && (data.backgroundId || data.background_id) &&
          !(data.backgroundImage || data.background_image)) {
        console.log('🖥️ DEBUG: backgroundId found but no image - fetching background details directly');

        const backgroundId = data.backgroundId || data.background_id;
        try {
          const backgroundResponse = await fetch(`https://${_host}:6003/api/${_company}/backgrounds/${backgroundId}`);
          if (backgroundResponse.ok) {
            const background = await backgroundResponse.json();
            console.log('🖥️ DEBUG: Fetched background directly:', background);

            // Enrich the display data with background info
            data.backgroundImage = background.url;
            data.backgroundType = background.type;
            data.background_image = background.url;
            data.background_type = background.type;

            console.log('🖥️ DEBUG: Enriched display data with background:', {
              backgroundImage: data.backgroundImage,
              backgroundType: data.backgroundType
            });
          } else {
            console.warn('🖥️ DEBUG: Failed to fetch background directly:', backgroundResponse.status);
          }
        } catch (bgError) {
          console.error('🖥️ DEBUG: Error fetching background directly:', bgError);
        }
      }

      setDisplayData(data);
      
      // If there's an active event, fetch the full event data to get heats information

      if (data && data.event_id && !data.waiting) {
        const eventResponse = await fetch(`https://${_host}:6003/api/${_company}/events/${data.event_id}`);
        
        if (eventResponse.ok) {
          const eventResult = await eventResponse.json();
          setEventData(eventResult);
          
          // Generate heats data

          if (eventResult.dancers && eventResult.dancers.length > 0 && eventResult.dances && eventResult.dances.length > 0) {
            const generatedHeats = generateHeats(eventResult);
            setHeats(generatedHeats);
          }
        }
      } else {
        // Only clear event data if we don't already have it
        // This prevents losing event data during background changes
        if (!eventData) {
          setEventData(null);
        }
        // Don't clear heats if we already have them
        if (heats.length === 0) {
          setHeats([]);
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error("Error fetching display data:", err);
      setError("Failed to connect to competition data. Please refresh the page.");
      setLoading(false);
    }
  };

  // Update competition thumbnail when selected competition changes
  useEffect(() => {
    if (selectedCompetition && selectedCompetition.thumbnail) {
      setCompetitionThumbnail(selectedCompetition.thumbnail);
    } else {
      setCompetitionThumbnail(null);
    }
  }, [selectedCompetition]);

  // Set up initial fetch and polling
  useEffect(() => {
    // Initial fetch
    fetchDisplayData();

    // Set up polling to check for updates every 2 seconds
    const interval = setInterval(fetchDisplayData, 2000);

    // Clean up on unmount
    return () => clearInterval(interval);
  }, []);

  // Listen for immediate refresh events from BackgroundControl
  useEffect(() => {
    const handleDisplayDataUpdated = (event) => {
            const { action, value } = event.detail || {};

      // Add a small delay to ensure backend has processed the update
      setTimeout(() => {
        fetchDisplayData();

        // For toggle actions, add a second fetch to ensure we get the updated value
        if (action === 'toggleIdleDisplay') {
          setTimeout(() => {
                        fetchDisplayData();
          }, 1000);
        }
      }, 500);
    };

    window.addEventListener('displayDataUpdated', handleDisplayDataUpdated);

    return () => {
      window.removeEventListener('displayDataUpdated', handleDisplayDataUpdated);
    };
  }, []);

  // Fetch background details when we have background_id but no background_image
  useEffect(() => {
    console.log('🖥️ DisplayScreen useEffect triggered, displayData:', displayData);

    if (displayData && (displayData.background_id || displayData.backgroundId)) {
      const backgroundId = displayData.background_id || displayData.backgroundId;
      const hasImage = displayData.background_image || displayData.backgroundImage;

      console.log('🖥️ Background check - backgroundId:', backgroundId, 'hasImage:', hasImage);

      // Only fetch if we have an ID but no image data
      if (backgroundId && !hasImage) {

        const fetchBackgroundDetails = async () => {
          try {
            console.log('🖥️ Fetching background details for ID:', backgroundId);
            const response = await fetch(`https://${_host}:6003/api/${_company}/backgrounds/${backgroundId}`);
            if (response.ok) {
              const background = await response.json();
              console.log('🖥️ Background details fetched:', background);
              setBackgroundDetails(background);
            } else {
              console.warn('❌ Failed to fetch background details:', response.status);
              setBackgroundDetails(null);
            }
          } catch (error) {
            console.error('❌ Error fetching background details:', error);
            setBackgroundDetails(null);
          }
        };

        fetchBackgroundDetails();
      } else {
        console.log('🖥️ Clearing background details - backgroundId:', backgroundId, 'hasImage:', hasImage);
        // Clear background details if we don't have a background_id or we already have image data
        if (!backgroundId) {
          setBackgroundDetails(null);
        }
      }
    } else {
      console.log('🖥️ No background_id in displayData');
    }
  }, [displayData?.background_id, displayData?.backgroundId, displayData?.background_image, displayData?.backgroundImage]);

  // Calculate current heat information
  const getCurrentHeatInfo = () => {

    // Competition completion is determined by event active status, not a completed field
    const isCompleted = false;

    // If we have cached event data and heats, use them even if displayData.event_id is null
    // This prevents losing heat info during background changes
    if (!displayData || displayData.waiting || isCompleted) {
      return null;
    }

    // Show heat info if we have event data and heats, even if event_id is temporarily null
    if (!eventData || heats.length === 0) {
      return null;
    }

    // Use the navigation indices from display data to show current dance and heat
    const currentDanceIndex = displayData.currentDanceIndex || displayData.current_dance_index || 0;
    const currentHeatIndex = displayData.currentHeatIndex || displayData.current_heat_index || 0;

    if (heats.length > currentDanceIndex && heats[currentDanceIndex]) {
      const currentDance = heats[currentDanceIndex];

      if (currentDance.heats && currentDance.heats.length > currentHeatIndex && currentDance.heats[currentHeatIndex]) {
        const currentHeat = currentDance.heats[currentHeatIndex];

        const heatInfo = {
          danceName: currentDance.dance,
          heatNumber: currentHeat.heatNumber,
          dancers: currentHeat.dancers.map(d => d.clothNumber),
          totalDances: heats.length,
          totalHeats: currentDance.heats.length,
          currentDanceIndex: currentDanceIndex,
          currentHeatIndex: currentHeatIndex,
          eventName: eventData.name
        };

        return heatInfo;
      }
    }

    return null;
  };

  // Calculate upcoming information
  const getUpcomingInfo = () => {
    if (!displayData || displayData.waiting) {
      return null;
    }

    const currentDanceIndex = displayData.currentDanceIndex || displayData.current_dance_index || 0;
    const currentHeatIndex = displayData.currentHeatIndex || displayData.current_heat_index || 0;
    const totalDances = displayData.totalDances || displayData.total_dances || 1;

    // If we have heats data, use detailed logic
    if (heats.length > 0 && heats[currentDanceIndex]) {
      const currentDance = heats[currentDanceIndex];
      const isLastHeat = currentHeatIndex >= currentDance.heats.length - 1;
      const isLastDance = currentDanceIndex >= heats.length - 1;
      
      // Next heat in same dance
      if (!isLastHeat && currentHeatIndex + 1 < currentDance.heats.length) {
        const nextHeat = currentDance.heats[currentHeatIndex + 1];
        return {
          type: 'next_heat',
          danceName: currentDance.dance,
          heatNumber: nextHeat.heatNumber,
          dancers: nextHeat.dancers.map(d => d.clothNumber)
        };
      }
      
      // Next dance, first heat
      if (isLastHeat && !isLastDance && currentDanceIndex + 1 < heats.length) {
        const nextDance = heats[currentDanceIndex + 1];
        const nextHeat = nextDance.heats[0];
        return {
          type: 'next_dance_detailed',
          danceName: nextDance.dance,
          heatNumber: nextHeat.heatNumber,
          dancers: nextHeat.dancers.map(d => d.clothNumber)
        };
      }
      
      // Final dance/heat - no next heat to show
      return null;
    }
    
    // Fallback to simple logic if no heats data
    if (currentDanceIndex < totalDances - 1) {
      return {
        type: 'next_dance',
        danceNumber: currentDanceIndex + 2,
        totalDances: totalDances,
        dancers: displayData.dancers || []
      };
    }
    
    // If it's the last dance, no next heat to show
    return null;
  };

  // Function to render background media
  const renderBackgroundMedia = () => {
    console.log('🖼️ DEBUG: renderBackgroundMedia called!');
    console.log('🖼️ DEBUG: displayData exists:', !!displayData);
    console.log('🖼️ DEBUG: backgroundDetails exists:', !!backgroundDetails);

    if (!displayData) {
      console.log('🖼️ DEBUG: No displayData, returning null');
      return null;
    }

    // Check both camelCase and snake_case field names from displayData
    let backgroundType = displayData.backgroundType || displayData.background_type;
    let backgroundImage = displayData.backgroundImage || displayData.background_image;

    console.log('🖼️ DEBUG: Initial backgroundType:', backgroundType);
    console.log('🖼️ DEBUG: Initial backgroundImage:', backgroundImage);

    // If we don't have background data but have fetched background details, use those
    if (!backgroundImage && backgroundDetails) {
      console.log('🖼️ DEBUG: Using backgroundDetails as fallback');
      backgroundType = backgroundDetails.type || 'image';
      backgroundImage = backgroundDetails.url || backgroundDetails.image_url;
      console.log('🖼️ DEBUG: Fallback backgroundType:', backgroundType);
      console.log('🖼️ DEBUG: Fallback backgroundImage:', backgroundImage);
          }


    // Fix duplicate protocol issue: check if backgroundImage already has a full URL
    let imageUrl = backgroundImage;
    if (backgroundImage) {
      console.log('🎬 Original backgroundImage:', backgroundImage);

      // Simple and robust URL construction
      if (backgroundImage.startsWith('http')) {
        // Already a full URL, just clean up common issues
        imageUrl = backgroundImage
          .replace('https://https://', 'https://')
          .replace('http://http://', 'http://')
          .replace('https://http://', 'http://')
          .replace('http://https://', 'https://')
          .replace('https//', 'https://')
          .replace('http//', 'http://');
      } else {
        // Relative path - construct full URL
        // Remove leading slash if it exists to avoid double slashes
        const cleanPath = backgroundImage.startsWith('/') ? backgroundImage.slice(1) : backgroundImage;
        imageUrl = `https://${_host}/${cleanPath}`;
      }

      console.log('🎬 Final imageUrl:', imageUrl);
    }

    if (!backgroundImage) {
      console.log('🖼️ DEBUG: No backgroundImage to render, returning null');
      return null;
    }

    console.log('🖼️ DEBUG: Rendering background media - type:', backgroundType, 'imageUrl:', imageUrl);

    if (backgroundType === 'video') {
      console.log('🖼️ DEBUG: Rendering video element with src:', imageUrl);
      return (
        <video
          src={imageUrl}
          className="background-video"
          autoPlay
          loop
          muted
          playsInline
        />
      );
    } else {
      console.log('🖼️ DEBUG: Rendering image element with url:', imageUrl);
      return (
        <div
          className="background-image"
          style={{ backgroundImage: `url(${imageUrl})` }}
        />
      );
    }
  };

  console.log('🖼️ DEBUG: DisplayScreen render - loading:', loading, 'error:', error, 'displayData:', !!displayData);

  if (loading) {
    return (
      <div className="display-screen loading">
        <div className="loading-spinner"></div>
        <h2>Connecting to competition data...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="display-screen error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  // Get background styles for CSS fallback
  const getBackgroundStyle = () => {
    if (!displayData) return {};

    // Check both camelCase and snake_case field names from displayData
    let backgroundType = displayData.backgroundType || displayData.background_type;
    let backgroundImage = displayData.backgroundImage || displayData.background_image;

    // If we don't have background data but have fetched background details, use those
    if (!backgroundImage && backgroundDetails) {
      backgroundType = backgroundDetails.type || 'image';
      backgroundImage = backgroundDetails.url || backgroundDetails.image_url;
    }

    // If we're using video background or the new media element rendering,
    // don't add background styles (they'll be handled by the video/image element)
    if ((backgroundImage && backgroundType === 'video') || renderBackgroundMedia()) {
      return {};
    }

    // Otherwise use CSS background for images
    if (backgroundImage && backgroundType !== 'video') {
      // Apply the same simplified URL fixing logic as renderBackgroundMedia
      let backgroundImageUrl = backgroundImage;
      if (backgroundImageUrl) {
        console.log('🎬 Original backgroundImage (CSS):', backgroundImage);

        // Simple and robust URL construction
        if (backgroundImageUrl.startsWith('http')) {
          // Already a full URL, just clean up common issues
          backgroundImageUrl = backgroundImageUrl
            .replace('https://https://', 'https://')
            .replace('http://http://', 'http://')
            .replace('https://http://', 'http://')
            .replace('http://https://', 'https://')
            .replace('https//', 'https://')
            .replace('http//', 'http://');
        } else {
          // Relative path - construct full URL
          // Remove leading slash if it exists to avoid double slashes
          const cleanPath = backgroundImageUrl.startsWith('/') ? backgroundImageUrl.slice(1) : backgroundImageUrl;
          backgroundImageUrl = `https://${_host}/${cleanPath}`;
        }

        console.log('🎬 Final backgroundImageUrl (CSS):', backgroundImageUrl);
      }
      return {
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }
    
    // Default backgrounds for different states
    if (displayData.waiting) {
      return { backgroundColor: '#1a237e' }; // Default blue background for waiting
    }
    
    // Competition completion is handled by event active status, not completed field
    
    return { backgroundColor: '#1a237e' }; // Default active background
  };

  // Check if we should show clean idle display (background only)
  // This should take precedence over all other conditions to ensure no text is shown
  const showIdleDisplay = displayData.showIdleDisplay || displayData.show_idle_display;

  
  if (displayData && showIdleDisplay) {
        console.log('🖼️ DEBUG: Rendering idle-clean display, calling renderBackgroundMedia');
        return (
      <div className="display-screen idle-clean" style={getBackgroundStyle()}>
        {renderBackgroundMedia()}
      </div>
    );
  }

  // Standard idle state with thumbnail or waiting message
  // More precise condition: show idle if waiting, but not if there's an active event
  if (!displayData || displayData.waiting) {
    console.log('🖼️ DEBUG: Rendering idle display, calling renderBackgroundMedia');
    return (
      <div className="display-screen idle" style={getBackgroundStyle()}>
        {renderBackgroundMedia()}
        {competitionThumbnail ? (
          <div className="idle-thumbnail-container">
            <img
              src={competitionThumbnail}
              alt="Competition Thumbnail"
              className="idle-thumbnail"
              onError={(e) => {
                e.target.style.display = 'none';
                // Show fallback message when thumbnail fails to load
                const parent = e.target.parentElement;
                if (parent && !parent.querySelector('.fallback-message')) {
                  const fallback = document.createElement('div');
                  fallback.className = 'fallback-message';
                  fallback.innerHTML = `
                    <h1>Flokraft</h1>
                    <p>Waiting for next event to start...</p>
                    <div class="idle-animation"></div>
                  `;
                  parent.appendChild(fallback);
                }
              }}
            />
          </div>
        ) : (
          <div className="idle-message">
            <h1>Flokraft</h1>
            <p>Waiting for next event to start...</p>
            <div className="idle-animation"></div>
          </div>
        )}
      </div>
    );
  }

  // Competition completion is handled by event active status, not a separate completed field

  const upcomingInfo = getUpcomingInfo();
  const currentHeatInfo = getCurrentHeatInfo();

  console.log('🖼️ DEBUG: Rendering active display, calling renderBackgroundMedia');
  return (
    <div className="display-screen active" style={getBackgroundStyle()}>
      {renderBackgroundMedia()}
      <div className="display-header">
        <h1>{currentHeatInfo ? currentHeatInfo.eventName : (displayData.eventName || '')} {displayData.roundType || ''}</h1>
      </div>

      <div className="display-content">
        <div className="dance-info">
          <div className="heat-number">
            {currentHeatInfo ? (
              `${currentHeatInfo.danceName} Heat ${currentHeatInfo.heatNumber} of ${currentHeatInfo.totalHeats}`
            ) : (
              `${displayData.danceName || 'Dance'} Heat ${displayData.heatNumber || 1} of ${displayData.totalHeats || 1}`
            )}
          </div>
          {/* Last Heat Indicator */}
          {(() => {
            // Check if we're on the last heat of the last dance
            const currentDanceIndex = (currentHeatInfo && currentHeatInfo.currentDanceIndex) ||
                                    (displayData && displayData.currentDanceIndex) ||
                                    (displayData && displayData.current_dance_index) || 0;
            const currentHeatIndex = (currentHeatInfo && currentHeatInfo.currentHeatIndex) ||
                                   (displayData && displayData.currentHeatIndex) ||
                                   (displayData && displayData.current_heat_index) || 0;
            const totalDances = (currentHeatInfo && currentHeatInfo.totalDances) ||
                              (displayData && displayData.totalDances) ||
                              (displayData && displayData.total_dances) || 5;
            const totalHeats = (currentHeatInfo && currentHeatInfo.totalHeats) ||
                             (displayData && displayData.totalHeats) ||
                             (displayData && displayData.total_heats) || 1;

            const isLastDance = currentDanceIndex + 1 === totalDances;
            const isLastHeat = currentHeatIndex + 1 === totalHeats;
            const showIndicator = isLastDance && isLastHeat;

            return showIndicator ? (
              <div className="last-heat-indicator">
                🏁 LAST HEAT 🏁
              </div>
            ) : null;
          })()}
        </div>

        <div className="dancers-display">
          <div className="dancer-numbers-grid">
            {(currentHeatInfo ? currentHeatInfo.dancers : (displayData.dancers || [])).map((clothNumber) => (
              <div key={clothNumber} className="dancer-number">
                {clothNumber}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="display-footer">
        <div className="progress-indicator">
          {upcomingInfo && upcomingInfo.type === 'next_heat' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <span style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '64px' }}>
                Next: {upcomingInfo.danceName} Heat {upcomingInfo.heatNumber}
              </span>
              <span style={{ fontSize: '64px', color: '#c5cae9' }}>
                Numbers: {upcomingInfo.dancers.join(', ')}
              </span>
            </div>
          ) : upcomingInfo && upcomingInfo.type === 'next_dance_detailed' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <span style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '64px' }}>
                Next: {upcomingInfo.danceName} Heat {upcomingInfo.heatNumber}
              </span>
              <span style={{ fontSize: '64px', color: '#c5cae9' }}>
                Numbers: {upcomingInfo.dancers.join(', ')}
              </span>
            </div>
          ) : upcomingInfo && upcomingInfo.type === 'next_dance' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <span style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '64px' }}>
                Next: Dance {upcomingInfo.danceNumber} of {upcomingInfo.totalDances}
              </span>
              <span style={{ fontSize: '64px', color: '#c5cae9' }}>
                Numbers: {upcomingInfo.dancers.join(', ')}
              </span>
            </div>
          ) : (
            <span>Dance {currentHeatInfo ? (currentHeatInfo.currentDanceIndex + 1) : (displayData.currentDanceIndex + 1)} of {currentHeatInfo ? currentHeatInfo.totalDances : (displayData.totalDances || 1)}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DisplayScreen;